import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { Toaster } from "sonner";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import AgentManagement from "@/pages/AgentManagement";
import ClientsPage from "@/pages/ClientsPage";
import ClientDetailPage from "@/pages/ClientDetailPage";
import CSVUploadPage from "@/pages/CSVUploadPage";
import CallLogsPage from "@/pages/CallLogsPage";
import ReportsPage from "@/pages/ReportsPage";
import SettingsPage from "@/pages/SettingsPage";
import AutoDialerPage from "@/pages/AutoDialerPage";
import ConversionsPage from "@/pages/ConversionsPage";
import ConvertClientPage from "@/pages/ConvertClientPage";
import DialpadPage from "@/pages/DialpadPage";
import SalaryPage from "@/pages/SalaryPage";
import AgentSalaryPage from "@/pages/AgentSalaryPage";
import ChatPage from "@/pages/ChatPage";
import SubAdminPage from "@/pages/SubAdminPage";
import AutomationBuilderPage from "@/pages/AutomationBuilderPage";
import B2CReportsPage from "@/pages/B2CReportsPage";
import BroadcastReportsPage from "@/pages/BroadcastReportsPage";
import Layout from "@/components/Layout";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen bg-background"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user || user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<DashboardPage />} />
        <Route path="agents" element={<AdminRoute><AgentManagement /></AdminRoute>} />
        <Route path="clients" element={<ClientsPage />} />
        <Route path="clients/:userId" element={<ClientDetailPage />} />
        <Route path="b2c-reports" element={<B2CReportsPage />} />
        <Route path="broadcast-reports" element={<BroadcastReportsPage />} />
        <Route path="upload" element={<AdminRoute><CSVUploadPage /></AdminRoute>} />
        <Route path="call-logs" element={<CallLogsPage />} />
        <Route path="reports" element={<AdminRoute><ReportsPage /></AdminRoute>} />
        <Route path="automations" element={<AdminRoute><AutomationBuilderPage /></AdminRoute>} />
        <Route path="settings" element={<AdminRoute><SettingsPage /></AdminRoute>} />
        <Route path="auto-dialer" element={<AdminRoute><AutoDialerPage /></AdminRoute>} />
        <Route path="conversions" element={<AdminRoute><ConversionsPage /></AdminRoute>} />
        <Route path="convert" element={<ConvertClientPage />} />
        <Route path="dialpad" element={<DialpadPage />} />
        <Route path="salary" element={<AdminRoute><SalaryPage /></AdminRoute>} />
        <Route path="sub-admins" element={<AdminRoute><SubAdminPage /></AdminRoute>} />
        <Route path="my-salary" element={<AgentSalaryPage />} />
        <Route path="chat" element={<ChatPage />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
        <Toaster position="top-right" richColors closeButton />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
